import React, { useState, useEffect } from 'react';
import { HiOutlineCheck, HiOutlineArrowSmRight } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { FaWhatsapp, FaYoutube, FaTelegram } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { FiPlus } from 'react-icons/fi';
import Modal from '../../../common/Modal';
import { toastService } from '../../../../services/toast';
import { settingsApi } from '../../../../services/api/settings.api';

const SeoSettings = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // الإعدادات الأساسية
  const [settings, setSettings] = useState({
    siteTitle: '',
    homePageTitle: '',
    siteDescription: '',
    keywords: '',
    socialLinks: {
      whatsapp: { enabled: false, url: '', name: 'واتساب', key: 'whatsapp' },
      twitter: { enabled: false, url: '', name: 'تويتر', key: 'twitter' },
      youtube: { enabled: false, url: '', name: 'يوتيوب', key: 'youtube' },
      telegram: { enabled: false, url: '', name: 'تليجرام', key: 'telegram' },
      tiktok: { enabled: false, url: '', name: 'تيك توك', key: 'tiktok' },
      facebook: { enabled: false, url: '', name: 'فيسبوك', key: 'facebook' },
      snapchat: { enabled: false, url: '', name: 'سناب شات', key: 'snapchat' },
      instagram: { enabled: false, url: '', name: 'انستجرام', key: 'instagram' }
    }
  });

  const [newPlatform, setNewPlatform] = useState({
    name: '',
    url: ''
  });

  const [editPlatform, setEditPlatform] = useState({
    key: '',
    name: '',
    url: ''
  });

  // جلب الإعدادات
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const data = await settingsApi.getSeoSettings();
        
        // تحويل المنصات من مصفوفة لـ object
        const socialLinks = {
          whatsapp: { enabled: false, url: '', name: 'واتساب', key: 'whatsapp' },
          twitter: { enabled: false, url: '', name: 'تويتر', key: 'twitter' },
          youtube: { enabled: false, url: '', name: 'يوتيوب', key: 'youtube' },
          telegram: { enabled: false, url: '', name: 'تليجرام', key: 'telegram' },
          tiktok: { enabled: false, url: '', name: 'تيك توك', key: 'tiktok' },
          facebook: { enabled: false, url: '', name: 'فيسبوك', key: 'facebook' },
          snapchat: { enabled: false, url: '', name: 'سناب شات', key: 'snapchat' },
          instagram: { enabled: false, url: '', name: 'انستجرام', key: 'instagram' }
        };

        if (data.socialLinks) {
          Object.entries(data.socialLinks).forEach(([key, platform]) => {
            if (socialLinks[key]) {
              socialLinks[key] = {
                ...socialLinks[key],
                ...platform,
                key
              };
            } else {
              socialLinks[key] = {
                ...platform,
                key
              };
            }
          });
        }

        setSettings({
          siteTitle: data.siteTitle || '',
          homePageTitle: data.homePageTitle || '',
          siteDescription: data.siteDescription || '',
          keywords: data.keywords || '',
          socialLinks
        });
      } catch (error) {
        console.error('Error fetching settings:', error);
        if (error.response?.status === 403) {
          toastService.error('غير مصرح لك بالوصول إلى هذه الصفحة');
          navigate('/');
        } else {
          toastService.error('حدث خطأ أثناء تحميل الإعدادات');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setSettings(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    setIsLoading(true);
    try {
      await settingsApi.updateSeoSettings(settings);
      toastService.success('تم حفظ التغييرات بنجاح');
    } catch (error) {
      console.error('Error saving settings:', error);
      if (error.response?.data?.errors) {
        // عرض أول رسالة خطأ من الـ validation
        const errorMessage = Object.values(error.response.data.errors)[0][0];
        toastService.error(errorMessage);
      } else if (error.response?.data?.message) {
        // عرض رسالة الخطأ العامة
        toastService.error(error.response.data.message);
      } else {
        toastService.error('حدث خطأ أثناء حفظ التغييرات');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPlatform = async () => {
    if (!newPlatform.name || !newPlatform.url) {
      toastService.error('برجاء إدخال اسم المنصة والرابط');
      return;
    }
    
    try {
      setIsLoading(true);
      // تحويل الاسم لـ key صالح (حروف صغيرة، بدون مسافات أو أحرف خاصة)
      const platformKey = newPlatform.name
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

      // التحقق من وجود المنصة
      if (settings.socialLinks[platformKey]) {
        toastService.error('هذه المنصة موجودة بالفعل');
        return;
      }

      const platformData = {
        key: platformKey,
        name: newPlatform.name,
        url: newPlatform.url,
        enabled: true
      };
      
      const response = await settingsApi.addSocialPlatform(platformData);
      
      setSettings(prev => ({
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          [platformKey]: {
            name: response.name,
            url: response.url,
            enabled: response.enabled,
            key: platformKey
          }
        }
      }));
      
      setNewPlatform({ name: '', url: '' });
      setIsModalOpen(false);
      toastService.success('تم إضافة المنصة الجديدة');
    } catch (error) {
      console.error('Error adding platform:', error);
      if (error.response?.data?.errors) {
        // عرض رسالة الخطأ من الـ validation
        const errorMessage = Object.values(error.response.data.errors)[0][0];
        toastService.error(errorMessage);
      } else {
        toastService.error('حدث خطأ أثناء إضافة المنصة');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePlatform = async (platformKey, e) => {
    if (e) e.preventDefault();
    try {
      setIsLoading(true);
      await settingsApi.deleteSocialPlatform(platformKey);
      
      setSettings(prev => {
        const newSocialLinks = { ...prev.socialLinks };
        delete newSocialLinks[platformKey];
        return {
          ...prev,
          socialLinks: newSocialLinks
        };
      });
      
      toastService.success('تم حذف المنصة');
    } catch (error) {
      console.error('Error deleting platform:', error);
      toastService.error('حدث خطأ أثناء حذف المنصة');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePlatform = (platformKey, enabled) => {
    setSettings(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platformKey]: {
          ...prev.socialLinks[platformKey],
          enabled
        }
      }
    }));
  };

  const handleEditPlatform = async () => {
    if (!editPlatform.name || !editPlatform.url) {
      toastService.error('برجاء إدخال اسم المنصة والرابط');
      return;
    }

    try {
      setIsLoading(true);
      const response = await settingsApi.updateSocialPlatform(editPlatform.key, {
        name: editPlatform.name,
        url: editPlatform.url
      });

      setSettings(prev => ({
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          [editPlatform.key]: {
            ...prev.socialLinks[editPlatform.key],
            name: editPlatform.name,
            url: editPlatform.url
          }
        }
      }));

      setIsEditModalOpen(false);
      toastService.success('تم تعديل المنصة بنجاح');
    } catch (error) {
      console.error('Error updating platform:', error);
      toastService.error('حدث خطأ أثناء تعديل المنصة');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (key, e) => {
    if (e) e.preventDefault();
    const platform = settings.socialLinks[key];
    setEditPlatform({
      key,
      name: platform.name,
      url: platform.url
    });
    setIsEditModalOpen(true);
  };

  return (
    <div>
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">إعدادات SEO</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate('/admin/dashboard/settings')}
              className="group relative p-1 text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
            >
              <HiOutlineArrowSmRight className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                رجوع للإعدادات
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="group relative p-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-500 dark:hover:text-emerald-400"
            >
              <HiOutlineCheck className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                حفظ التغييرات
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          {/* معلومات الموقع الأساسية */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">معلومات الموقع الأساسية</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  عنوان الموقع
                </label>
                <input
                  type="text"
                  name="siteTitle"
                  value={settings.siteTitle}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="أدخل عنوان الموقع"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  عنوان الصفحة الرئيسية
                </label>
                <input
                  type="text"
                  name="homePageTitle"
                  value={settings.homePageTitle}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="أدخل عنوان الصفحة الرئيسية"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  وصف الموقع
                </label>
                <textarea
                  name="siteDescription"
                  value={settings.siteDescription}
                  onChange={handleInputChange}
                  rows={3}
                  className="input-base"
                  placeholder="أدخل وصفاً مختصراً للموقع"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  الكلمات المفتاحية
                </label>
                <div className="relative">
                  <textarea
                    name="keywords"
                    value={settings.keywords}
                    onChange={handleInputChange}
                    rows={3}
                    className="input-base"
                    placeholder="أدخل الكلمات المفتاحية مفصولة بفواصل"
                    dir="rtl"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400 text-sm">,</span>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  افصل بين الكلمات المفتاحية بفواصل. مثال: تسويق, تجارة إلكترونية, متجر
                </p>
              </div>
            </div>
          </div>

          {/* روابط التواصل الاجتماعي */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">روابط التواصل الاجتماعي</h3>
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsModalOpen(true);
                }}
                className="w-8 h-8 rounded-md bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 flex items-center justify-center"
              >
                <FiPlus className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* واتساب */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                      <FaWhatsapp className="w-4 h-4 text-green-500" />
                    </div>
                    <h4 className="font-medium">واتساب</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.socialLinks.whatsapp.enabled}
                        onChange={(e) => {
                          const { checked } = e.target;
                          handleTogglePlatform('whatsapp', checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-green-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                    <button
                      onClick={(e) => openEditModal('whatsapp', e)}
                      className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="url"
                  name="socialLinks.whatsapp.url"
                  value={settings.socialLinks.whatsapp.url}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="https://wa.me/..."
                  dir="ltr"
                />
              </div>

              {/* تويتر */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                      <FaXTwitter className="w-4 h-4 text-black-500" />
                    </div>
                    <h4 className="font-medium">X (تويتر)</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.socialLinks.twitter.enabled}
                        onChange={(e) => {
                          const { checked } = e.target;
                          handleTogglePlatform('twitter', checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                    <button
                      onClick={(e) => openEditModal('twitter', e)}
                      className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-twitter-x" viewBox="0 0 16 16">
                        <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="url"
                  name="socialLinks.twitter.url"
                  value={settings.socialLinks.twitter.url}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="https://x.com/..."
                  dir="ltr"
                />
              </div>

              {/* يوتيوب */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                      <FaYoutube className="w-4 h-4 text-red-500" />
                    </div>
                    <h4 className="font-medium">يوتيوب</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.socialLinks.youtube.enabled}
                        onChange={(e) => {
                          const { checked } = e.target;
                          handleTogglePlatform('youtube', checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-red-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                    <button
                      onClick={(e) => openEditModal('youtube', e)}
                      className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="url"
                  name="socialLinks.youtube.url"
                  value={settings.socialLinks.youtube.url}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="https://youtube.com/..."
                  dir="ltr"
                />
              </div>

              {/* تليجرام */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                      <FaTelegram className="w-4 h-4 text-blue-500" />
                    </div>
                    <h4 className="font-medium">تليجرام</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.socialLinks.telegram.enabled}
                        onChange={(e) => {
                          const { checked } = e.target;
                          handleTogglePlatform('telegram', checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                    <button
                      onClick={(e) => openEditModal('telegram', e)}
                      className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="url"
                  name="socialLinks.telegram.url"
                  value={settings.socialLinks.telegram.url}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="https://t.me/..."
                  dir="ltr"
                />
              </div>

              {/* تيك توك */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-black-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                      </svg>
                    </div>
                    <h4 className="font-medium">تيك توك</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.socialLinks.tiktok.enabled}
                        onChange={(e) => {
                          const { checked } = e.target;
                          handleTogglePlatform('tiktok', checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-purple-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                    <button
                      onClick={(e) => openEditModal('tiktok', e)}
                      className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="url"
                  name="socialLinks.tiktok.url"
                  value={settings.socialLinks.tiktok.url}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="https://tiktok.com/..."
                  dir="ltr"
                />
              </div>

              {/* فيسبوك */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 8H6v4h3v12h5V12h3.642L18 8h-4V6.333C14 5.378 14.192 5 15.115 5H18V0h-3.808C10.596 0 9 1.583 9 4.615V8z" />
                      </svg>
                    </div>
                    <h4 className="font-medium">فيسبوك</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.socialLinks.facebook.enabled}
                        onChange={(e) => {
                          const { checked } = e.target;
                          handleTogglePlatform('facebook', checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                    <button
                      onClick={(e) => openEditModal('facebook', e)}
                      className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="url"
                  name="socialLinks.facebook.url"
                  value={settings.socialLinks.facebook.url}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="https://facebook.com/..."
                  dir="ltr"
                />
              </div>

              {/* سناب شات */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-yellow-500" viewBox="0 0 512 512" fill="currentColor">
                      <path fill="#FFD43B" d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm169.5 338.9c-3.5 8.1-18.1 14-44.8 18.2-1.4 1.9-2.5 9.8-4.3 15.9-1.1 3.7-3.7 5.9-8.1 5.9h-.2c-6.2 0-12.8-2.9-25.8-2.9-17.6 0-23.7 4-37.4 13.7-14.5 10.3-28.4 19.1-49.2 18.2-21 1.6-38.6-11.2-48.5-18.2-13.8-9.7-19.8-13.7-37.4-13.7-12.5 0-20.4 3.1-25.8 3.1-5.4 0-7.5-3.3-8.3-6-1.8-6.1-2.9-14.1-4.3-16-13.8-2.1-44.8-7.5-45.5-21.4-.2-3.6 2.3-6.8 5.9-7.4 46.3-7.6 67.1-55.1 68-57.1 0-.1 .1-.2 .2-.3 2.5-5 3-9.2 1.6-12.5-3.4-7.9-17.9-10.7-24-13.2-15.8-6.2-18-13.4-17-18.3 1.6-8.5 14.4-13.8 21.9-10.3 5.9 2.8 11.2 4.2 15.7 4.2 3.3 0 5.5-.8 6.6-1.4-1.4-23.9-4.7-58 3.8-77.1C183.1 100 230.7 96 244.7 96c.6 0 6.1-.1 6.7-.1 34.7 0 68 17.8 84.3 54.3 8.5 19.1 5.2 53.1 3.8 77.1 1.1 .6 2.9 1.3 5.7 1.4 4.3-.2 9.2-1.6 14.7-4.2 4-1.9 9.6-1.6 13.6 0 6.3 2.3 10.3 6.8 10.4 11.9 .1 6.5-5.7 12.1-17.2 16.6-1.4 .6-3.1 1.1-4.9 1.7-6.5 2.1-16.4 5.2-19 11.5-1.4 3.3-.8 7.5 1.6 12.5 .1 .1 .1 .2 .2 .3 .9 2 21.7 49.5 68 57.1 4 1 7.1 5.5 4.9 10.8z"/>
                      </svg>
                    </div>
                    <h4 className="font-medium">سناب شات</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.socialLinks.snapchat.enabled}
                        onChange={(e) => {
                          const { checked } = e.target;
                          handleTogglePlatform('snapchat', checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-yellow-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                    <button
                      onClick={(e) => openEditModal('snapchat', e)}
                      className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="url"
                  name="socialLinks.snapchat.url"
                  value={settings.socialLinks.snapchat.url}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="https://snapchat.com/..."
                  dir="ltr"
                />
              </div>

              {/* انستجرام */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                      </svg>
                    </div>
                    <h4 className="font-medium">انستجرام</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.socialLinks.instagram.enabled}
                        onChange={(e) => {
                          const { checked } = e.target;
                          handleTogglePlatform('instagram', checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-pink-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                    <button
                      onClick={(e) => openEditModal('instagram', e)}
                      className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <input
                  type="url"
                  name="socialLinks.instagram.url"
                  value={settings.socialLinks.instagram.url}
                  onChange={handleInputChange}
                  className="input-base"
                  placeholder="https://instagram.com/..."
                  dir="ltr"
                />
              </div>

              {/* المنصات المضافة */}
              {Object.entries(settings.socialLinks).map(([key, platform]) => {
                if (key === 'whatsapp' || key === 'twitter' || key === 'youtube' || key === 'telegram' || key === 'tiktok' || key === 'facebook' || key === 'snapchat' || key === 'instagram') return null;
                
                return (
                  <div key={key} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-600 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-500">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-primary-500" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                            <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <h4 className="font-medium">{platform.name}</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={platform.enabled}
                            onChange={(e) => {
                              const { checked } = e.target;
                              handleTogglePlatform(key, checked);
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                        </label>
                        <button
                          onClick={(e) => handleDeletePlatform(key, e)}
                          className="p-1 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => openEditModal(key, e)}
                          className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <input
                      type="url"
                      value={platform.url}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          socialLinks: {
                            ...prev.socialLinks,
                            [key]: {
                              ...prev.socialLinks[key],
                              url: e.target.value
                            }
                          }
                        }));
                      }}
                      className="input-base"
                      placeholder="https://"
                      dir="ltr"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </form>

        {/* مودال إضافة منصة جديدة */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setNewPlatform({ name: '', url: '' });
            setIsModalOpen(false);
          }}
          title="إضافة منصة جديدة"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                اسم المنصة
              </label>
              <input
                type="text"
                className="input-base"
                placeholder="مثال: فيسبوك"
                value={newPlatform.name}
                onChange={(e) => setNewPlatform(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                رابط المنصة
              </label>
              <input
                type="url"
                className="input-base"
                placeholder="https://"
                dir="ltr"
                value={newPlatform.url}
                onChange={(e) => setNewPlatform(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              onClick={() => {
                setNewPlatform({ name: '', url: '' });
                setIsModalOpen(false);
              }}
            >
              إلغاء
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md focus:ring-2 focus:ring-primary-500"
              onClick={handleAddPlatform}
            >
              إضافة
            </button>
          </div>
        </Modal>

        {/* مودال تعديل منصة */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setEditPlatform({ key: '', name: '', url: '' });
            setIsEditModalOpen(false);
          }}
          title="تعديل المنصة"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                اسم المنصة
              </label>
              <input
                type="text"
                className="input-base"
                placeholder="مثال: فيسبوك"
                value={editPlatform.name}
                onChange={(e) => setEditPlatform(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                رابط المنصة
              </label>
              <input
                type="url"
                className="input-base"
                placeholder="https://"
                dir="ltr"
                value={editPlatform.url}
                onChange={(e) => setEditPlatform(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              onClick={() => {
                setEditPlatform({ key: '', name: '', url: '' });
                setIsEditModalOpen(false);
              }}
            >
              إلغاء
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md focus:ring-2 focus:ring-primary-500"
              onClick={handleEditPlatform}
            >
              حفظ التعديلات
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default SeoSettings;
